export function extractCommentText(value, depth = 0) {
    if (value == null) return '';
    if (typeof value === 'string') {
        return value === '[object Object]' ? '' : value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value !== 'object' || depth > 5) return '';

    const nested = value.comment ?? value.message ?? value.text ?? value.body ?? value.content;
    if (nested != null && nested !== value) {
        return extractCommentText(nested, depth + 1);
    }
    return '';
}

export function isTempCommentId(id) {
    return id == null || id === '' || String(id).startsWith('tmp-');
}

export function getCommentId(comment) {
    if (comment == null) return null;
    if (typeof comment !== 'object') return comment;
    const id = comment.id ?? comment.commentId ?? comment.comment_id ?? comment._id ?? null;
    return id == null || id === '' ? null : id;
}

export function getCommentAuthor(comment) {
    if (!comment || typeof comment !== 'object') return '';
    const author = comment.lastEditedBy
        || comment.userName
        || comment.username
        || comment.createdBy
        || comment.created_by
        || comment.addedBy
        || comment.editedBy
        || comment.user
        || comment.email
        || '';
    if (typeof author === 'string') {
        const trimmed = author.trim();
        return (!trimmed || trimmed === '[object Object]') ? '' : trimmed;
    }
    if (author && typeof author === 'object') {
        return author.name || author.email || author.userName || author.username || '';
    }
    return '';
}

export function isQuoteLikeRecord(value) {
    return !!(value && typeof value === 'object' && !Array.isArray(value)
        && (Array.isArray(value.lines) || Array.isArray(value.quoteUrls) || value.customer_name != null));
}

export function normalizeComment(comment) {
    if (!comment) return comment;
    if (typeof comment === 'string') {
        return { comment };
    }

    let source = comment;
    const nestedObject = comment.comment;
    if (nestedObject && typeof nestedObject === 'object' && !Array.isArray(nestedObject)
        && (nestedObject.id || nestedObject.commentId || nestedObject.comment || nestedObject.message || nestedObject.lastEditedBy)) {
        source = { ...comment, ...nestedObject };
    } else if (comment.data && typeof comment.data === 'object' && !Array.isArray(comment.data)
        && !isQuoteLikeRecord(comment.data)) {
        source = { ...comment, ...comment.data };
    }

    const id = getCommentId(source) || getCommentId(comment);
    return {
        ...source,
        id: id ?? undefined,
        commentId: source.commentId ?? source.comment_id ?? (!isTempCommentId(id) ? id : source.commentId),
        comment: extractCommentText(source),
        lastEditedBy: getCommentAuthor(source) || getCommentAuthor(comment) || '',
    };
}

function pickFromCommentList(list, fallback) {
    if (!Array.isArray(list) || list.length === 0) return null;
    const wanted = extractCommentText(fallback);
    const withRealId = list.filter((item) => !isTempCommentId(getCommentId(item)));
    if (wanted) {
        const match = [...withRealId].reverse().find((item) => extractCommentText(item) === wanted)
            || [...list].reverse().find((item) => extractCommentText(item) === wanted);
        if (match) return match;
    }
    return withRealId[withRealId.length - 1] || list[list.length - 1];
}

export function pickSavedComment(serverResponse, fallback = {}) {
    if (!serverResponse) return null;

    let candidate = serverResponse;
    if (Array.isArray(serverResponse)) {
        candidate = pickFromCommentList(serverResponse, fallback);
    } else if (isQuoteLikeRecord(serverResponse) && Array.isArray(serverResponse.comment)) {
        candidate = pickFromCommentList(serverResponse.comment, fallback);
    } else if (serverResponse.comment && Array.isArray(serverResponse.comment) && !extractCommentText(serverResponse)) {
        candidate = pickFromCommentList(serverResponse.comment, fallback);
    } else if (serverResponse.data && typeof serverResponse.data === 'object') {
        return pickSavedComment(serverResponse.data, fallback);
    }

    if (!candidate || isQuoteLikeRecord(candidate)) {
        return fallback && fallback.comment ? { ...fallback, id: getCommentId(fallback) || undefined } : null;
    }

    const normalized = normalizeComment(candidate);
    const text = extractCommentText(normalized) || extractCommentText(fallback) || '';
    const id = [getCommentId(normalized), getCommentId(fallback)].find((value) => !isTempCommentId(value));
    if (!id && !text) {
        return fallback && fallback.comment ? { ...fallback } : null;
    }

    return {
        ...normalized,
        ...(id != null ? { id, commentId: normalized.commentId || id } : {}),
        comment: text,
        lastEditedBy: getCommentAuthor(normalized) || fallback.lastEditedBy || '',
        dateStr: normalized.dateStr || fallback.dateStr,
        timeStr: normalized.timeStr || fallback.timeStr,
        status: normalized.status || fallback.status || '',
        resolved: normalized.resolved ?? fallback.resolved ?? false,
    };
}

function commentTimestamp(comment) {
    if (!comment || typeof comment !== 'object') return 0;
    const parsed = comment.dateStr && comment.timeStr
        ? new Date(`${comment.dateStr} ${comment.timeStr}`)
        : new Date(comment.createdAt || comment.date || 0);
    const time = parsed.getTime();
    return Number.isFinite(time) ? time : 0;
}

/** Newest first, the order the Orders grid already uses for a line's comments. */
export function sortCommentsNewestFirst(comments) {
    if (!Array.isArray(comments)) return [];
    return [...comments].sort((a, b) => commentTimestamp(b) - commentTimestamp(a));
}

export function toStoredComment(newComment, status = 'pending', extras = {}) {
    if (newComment && typeof newComment === 'object' && !Array.isArray(newComment)) {
        if (isQuoteLikeRecord(newComment)) {
            return toStoredComment(extractCommentText(newComment) || extras.comment || '', status, extras);
        }
        const normalized = normalizeComment(newComment);
        const id = getCommentId(normalized) || getCommentId(extras);
        return {
            ...normalized,
            ...(!isTempCommentId(id) ? { id, commentId: normalized.commentId || id } : {}),
            lastEditedBy: normalized.lastEditedBy || extras.lastEditedBy || 'Current User',
            dateStr: normalized.dateStr || extras.dateStr || new Date().toLocaleDateString(),
            timeStr: normalized.timeStr || extras.timeStr || new Date().toLocaleTimeString(),
            status: normalized.status || status,
            resolved: normalized.resolved ?? (status === 'resolved'),
        };
    }
    return {
        id: extras.id && !isTempCommentId(extras.id) ? extras.id : Date.now(),
        comment: typeof newComment === 'string' ? newComment : '',
        lastEditedBy: extras.lastEditedBy || 'Current User',
        dateStr: extras.dateStr || new Date().toLocaleDateString(),
        timeStr: extras.timeStr || new Date().toLocaleTimeString(),
        resolved: status === 'resolved',
        status,
    };
}
